import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowRight, Search, Filter, ExternalLink } from 'lucide-react';

const caseStudies = [
  {
    id: 1,
    title: 'IBPAP Industry Sustainability Program',
    industry: 'IT-BPM',
    solution: 'Reporting',
    region: 'Asia Pacific',
    featured: true,
    challenge: 'The IT and Business Process Association of the Philippines (IBPAP) needed to establish baseline sustainability reporting standards for the Philippine IT-BPM industry, which employs over 1.6 million professionals.',
    approach: 'SusTek served as the Sustainability Consultant for IBPAP, providing the IBPAP Sustainability Platform powered by SusTek technology. We conducted double materiality assessments, developed industry-specific ESG metrics, and guided pilot companies through their first sustainability reports aligned with IBPAP baseline standards.',
    results: [
      'Published inaugural IBPAP ESG Report (2025 Edition)',
      'Launched IT-BPM Sustainability Reporting Pilot Program',
      'Established industry-wide baseline reporting standards',
      'Onboarded pilot companies across small, mid-size, and large-scale BPO operations',
    ],
    metrics: { primary: 'Industry', secondary: 'First ESG Report' },
    image: 'https://images.unsplash.com/photo-1606857521015-7f9fcf423740?w=600&q=80',
    testimonial: {
      quote: 'This partnership marks a significant step forward in establishing sustainability as a core pillar of the Philippine IT-BPM industry, ensuring our member companies are prepared for the increasing client demands for ESG transparency.',
      author: 'Industry Partnership',
      company: 'IT and Business Process Association of the Philippines (IBPAP)',
    },
    partnershipDetails: {
      mouSigning: true,
      linkedinPost: 'https://www.linkedin.com/posts/ibpap_sustainability-partnership-itbpm-activity-7202224817519955968-joDP/',
      knowledgeHub: 'https://ibpap.org/knowledge-hub',
      publications: [
        'IBPAP ESG Report (2025 Edition)',
        'Baseline Set: Launching the IT-BPM Sustainability Mandate',
      ],
    },
  },
  {
    id: 2,
    title: 'Electronics Manufacturing in Taiwan',
    industry: 'Manufacturing',
    solution: 'Data Engineering',
    region: 'Asia Pacific',
    challenge: 'Fragmented data across 12 manufacturing sites with manual reporting processes.',
    approach: 'Implemented unified data pipelines with real-time emissions monitoring and automated GHG calculations.',
    results: ['40% time savings in reporting', '99.9% data accuracy', 'Audit-ready evidence packs'],
    metrics: { primary: '40%', secondary: 'Time Savings' },
    image: 'https://images.unsplash.com/photo-1693932038683-7c35401f5307?w=600&q=80',
    testimonial: {
      quote: 'SusTek transformed our sustainability reporting from a quarterly headache to an automated, continuous process.',
      author: 'Sustainability Director',
      company: 'Leading Electronics Manufacturer',
    },
  },
  {
    id: 3,
    title: 'Multi-Property Real Estate Portfolio',
    industry: 'Real Estate',
    solution: 'Reporting',
    region: 'Europe',
    challenge: 'GRESB reporting across 50+ commercial properties with inconsistent data quality.',
    approach: 'Deployed portfolio-wide data platform with tenant engagement and automated GRESB submissions.',
    results: ['5-star GRESB rating achieved', '30% energy reduction identified', 'Real-time portfolio monitoring'],
    metrics: { primary: '5-Star', secondary: 'GRESB Rating' },
    image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&q=80',
    testimonial: {
      quote: 'The platform gave us unprecedented visibility across our entire portfolio.',
      author: 'ESG Manager',
      company: 'European Property Fund',
    },
  },
  {
    id: 4,
    title: 'Carbon Credit Tokenization Project',
    industry: 'Public Sector',
    solution: 'Tokenization',
    region: 'Americas',
    challenge: 'Manual verification and double-counting risks in carbon credit issuance.',
    approach: 'Built end-to-end tokenization infrastructure with dMRV integration and registry connectivity.',
    results: ['100% traceability achieved', 'Zero double-counting incidents', '10x faster verification'],
    metrics: { primary: '100%', secondary: 'Traceability' },
    image: 'https://images.unsplash.com/photo-1644088379091-d574269d422f?w=600&q=80',
    testimonial: {
      quote: 'The SusTek tokenization platform brought unprecedented transparency to our carbon program.',
      author: 'Program Director',
      company: 'Regional Conservation Authority',
    },
  },
  {
    id: 5,
    title: 'Global Logistics Emissions Tracking',
    industry: 'Logistics',
    solution: 'dMRV',
    region: 'Global',
    challenge: 'Customer emissions reporting across multi-modal transportation networks.',
    approach: 'Implemented GLEC Framework-compliant tracking with automated customer reporting.',
    results: ['ISO 14083 compliance', '25% emissions reduction identified', 'Real-time customer portal'],
    metrics: { primary: '25%', secondary: 'Emissions Identified' },
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=80',
    testimonial: {
      quote: 'Our customers now have full visibility into their supply chain emissions.',
      author: 'Sustainability Lead',
      company: 'Global Freight Provider',
    },
  },
];

const industries = ['All', 'IT-BPM', 'Manufacturing', 'Real Estate', 'Logistics', 'Public Sector', 'Hospitality'];
const solutions = ['All', 'Data Engineering', 'Reporting', 'dMRV', 'Tokenization', 'Credibility Engine'];
const regions = ['All', 'Asia Pacific', 'Europe', 'Americas', 'Global'];

export const CaseStudies = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('All');
  const [selectedSolution, setSelectedSolution] = useState('All');
  const [selectedRegion, setSelectedRegion] = useState('All');

  const filteredStudies = caseStudies.filter((study) => {
    const matchesSearch =
      study.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      study.challenge.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesIndustry = selectedIndustry === 'All' || study.industry === selectedIndustry;
    const matchesSolution = selectedSolution === 'All' || study.solution === selectedSolution;
    const matchesRegion = selectedRegion === 'All' || study.region === selectedRegion;
    return matchesSearch && matchesIndustry && matchesSolution && matchesRegion;
  });

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-subtle">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <Badge variant="outline" className="mb-4">Case Studies</Badge>
            <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-6">
              Proven Results Across Industries
            </h1>
            <p className="text-xl text-muted-foreground">
              Discover how leading organizations have transformed their sustainability programs
              with SusTek's data engineering and credibility infrastructure.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-background border-b border-border sticky top-[72px] z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search case studies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex flex-wrap gap-3">
              <Select value={selectedIndustry} onValueChange={setSelectedIndustry}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Industry" />
                </SelectTrigger>
                <SelectContent>
                  {industries.map((industry) => (
                    <SelectItem key={industry} value={industry}>
                      {industry}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedSolution} onValueChange={setSelectedSolution}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Solution" />
                </SelectTrigger>
                <SelectContent>
                  {solutions.map((solution) => (
                    <SelectItem key={solution} value={solution}>
                      {solution}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedRegion} onValueChange={setSelectedRegion}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Region" />
                </SelectTrigger>
                <SelectContent>
                  {regions.map((region) => (
                    <SelectItem key={region} value={region}>
                      {region}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredStudies.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-8">
              {filteredStudies.map((study) => (
                <Card key={study.id} className="overflow-hidden hover-lift border-border/50 bg-card">
                  <div className="aspect-video relative overflow-hidden">
                    <img
                      src={study.image}
                      alt={study.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-card/90 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="secondary" className="bg-secondary/90 text-secondary-foreground">
                          {study.industry}
                        </Badge>
                        <Badge variant="outline" className="bg-card/90 text-foreground">
                          {study.solution}
                        </Badge>
                        <Badge variant="outline" className="bg-card/90 text-foreground">
                          {study.region}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <CardHeader>
                    <CardTitle className="text-xl">{study.title}</CardTitle>
                    <CardDescription className="text-base">{study.challenge}</CardDescription>
                  </CardHeader>
                  <CardContent className="flex flex-col flex-1">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="text-center p-4 rounded-lg bg-teal/10">
                        <p className="text-2xl font-bold text-teal">{study.metrics.primary}</p>
                        <p className="text-xs text-muted-foreground">{study.metrics.secondary}</p>
                      </div>
                      <ul className="flex-1 space-y-1">
                        {study.results.slice(0, 3).map((result, index) => (
                          <li key={index} className="text-sm text-muted-foreground flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-teal" />
                            {result}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    {/* Partnership Links for IBPAP Case Study */}
                    {study.partnershipDetails && (
                      <div className="mb-4 p-4 rounded-lg bg-teal/5 border border-teal/20">
                        <p className="text-sm font-semibold text-foreground mb-3">Partnership Resources</p>
                        <div className="space-y-2">
                          {study.partnershipDetails.linkedinPost && (
                            <a
                              href={study.partnershipDetails.linkedinPost}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-sm text-teal hover:underline"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                              View MOU Signing Announcement
                            </a>
                          )}
                          {study.partnershipDetails.knowledgeHub && (
                            <a
                              href={study.partnershipDetails.knowledgeHub}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-sm text-teal hover:underline"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                              IBPAP Knowledge Hub - ESG Report
                            </a>
                          )}
                        </div>
                        {study.partnershipDetails.publications && (
                          <div className="mt-3 pt-3 border-t border-teal/10">
                            <p className="text-xs text-muted-foreground mb-1">Published Reports:</p>
                            <ul className="space-y-1">
                              {study.partnershipDetails.publications.map((pub, idx) => (
                                <li key={idx} className="text-xs text-muted-foreground">• {pub}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="p-4 rounded-lg bg-muted/50 border border-border mt-auto">
                      <p className="text-sm italic text-muted-foreground mb-2">
                        &ldquo;{study.testimonial.quote}&rdquo;
                      </p>
                      <p className="text-xs text-foreground font-medium">
                        — {study.testimonial.author}, {study.testimonial.company}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-xl text-muted-foreground mb-4">No case studies found matching your criteria.</p>
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedIndustry('All');
                  setSelectedSolution('All');
                  setSelectedRegion('All');
                }}
              >
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-foreground mb-4">
            Ready to Write Your Success Story?
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Join leading organizations that have transformed their sustainability programs with SusTek.
          </p>
          <Link to="/book-call">
            <Button variant="teal" size="xl" className="group">
              Book a Discovery Call
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CaseStudies;
